/**
 * Alias username rules: length bounds, per-tier character sets, and
 * normalization.
 *
 * Usernames are always stored lowercase. Paid aliases may use hyphens and
 * underscores; free aliases are restricted to plain alphanumerics, matching the
 * shape of the names {@link generateUsername} hands them.
 *
 * Pure and shared so the create wizard, the name-check endpoint, and the create
 * endpoint all agree. The API re-validates everything — client-side checks are
 * UX only.
 *
 * @module constants/username-rules
 */
/** Shortest acceptable username. */
export const USERNAME_MIN_LENGTH = 3;
/** Longest acceptable username. Also the ceiling for generated names. */
export const USERNAME_MAX_LENGTH = 30;
/** Paid aliases: lowercase alphanumerics plus hyphen and underscore. */
export const USERNAME_CHARSET_PAID = /^[a-z0-9_-]+$/;
/** Free aliases: lowercase alphanumerics only. */
export const USERNAME_CHARSET_FREE = /^[a-z0-9]+$/;
/**
 * Canonical storage form. Callers must normalize before validating, comparing,
 * or persisting, so `Foo` and `foo` cannot diverge.
 */
export function normalizeUsername(raw) {
    return raw.trim().toLowerCase();
}
/** The character set a tier may use. */
export function usernameCharsetForTier(isPaid) {
    return isPaid ? USERNAME_CHARSET_PAID : USERNAME_CHARSET_FREE;
}
/**
 * Length and character-set validation for an already-normalized username.
 *
 * Does not cover the blocked-word list, availability, or the free-tier
 * generated-name requirement — those are server-side only.
 */
export function checkUsernameShape(username, isPaid) {
    if (username.length < USERNAME_MIN_LENGTH)
        return { ok: false, code: 'TOO_SHORT' };
    if (username.length > USERNAME_MAX_LENGTH)
        return { ok: false, code: 'TOO_LONG' };
    if (!usernameCharsetForTier(isPaid).test(username))
        return { ok: false, code: 'INVALID_CHARS' };
    return { ok: true };
}
/** Longest displayName we accept. */
export const DISPLAY_NAME_MAX_LENGTH = 50;
/** Shortest displayName we accept. */
export const DISPLAY_NAME_MIN_LENGTH = 1;
//# sourceMappingURL=username-rules.js.map